import { Box, BoxProps, Checkbox, Skeleton } from '@mui/material'
import CheckedIcon from 'components/shared/icons/CheckedIcon'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { QueryStatus } from 'react-query'
import { CellsByGroup } from 'redux/slices'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { PinnedMorphotype } from 'redux/slices/types'
import { useCellImageUrlFromCellId } from './tsv/utils'

const imageSize = 72
interface ActionPlotCellImageProps {
  pinnedGroupId?: number
  cellId: string
  imgProps?: React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>
  wrapperProps?: BoxProps
  handleStatusChange?(status?: QueryStatus): void
  /**
   * If width/height is not provided, use the default height/width of 72
   * @default true
   */
  revertToDefaultDimensions?: boolean
}

function findGroupName(groupId: number, cells: PinnedMorphotype[] | undefined): string | undefined {
  const activeGroup = cells?.find((group) => {
    return group.id === groupId
  })

  return activeGroup?.name
}

const ActionPlotCellImageComponent = ({
  pinnedGroupId,
  cellId,
  imgProps,
  wrapperProps,
  handleStatusChange,
  revertToDefaultDimensions = true,
}: ActionPlotCellImageProps): JSX.Element => {
  const { imageDataUrl, status } = useCellImageUrlFromCellId(cellId)
  const [activeGroup, setActiveGroup] = useState<CellsByGroup | undefined>()
  const [pinnedGroupName, setPinnedGroupName] = useState<string>('')

  const {
    appendCellToExport,
    cellVisualizations: { exportCellMorphotype, pinnedCells },
  } = useCellVisualizationsSlice()

  useEffect(() => {
    const currentGroup = exportCellMorphotype?.find(
      (morphotype) => morphotype.groupId === pinnedGroupId
    )

    if (currentGroup) {
      setActiveGroup(currentGroup)
    } else {
      setActiveGroup(undefined)
    }
  }, [exportCellMorphotype, pinnedGroupId])

  useEffect(() => {
    if (handleStatusChange) handleStatusChange(status)
  }, [handleStatusChange, status])

  const handleCellClicked = useCallback(() => {
    appendCellToExport({
      groupId: pinnedGroupId as number,
      cellId,
      groupName: pinnedGroupName,
    })
  }, [appendCellToExport, pinnedGroupId, cellId, pinnedGroupName])

  const defaultImageSize = useMemo(() => 
    revertToDefaultDimensions ? imageSize : undefined, 
    [revertToDefaultDimensions]
  )

  const showChecked = useMemo(() => {
    return activeGroup?.cellIds.includes(cellId)
  }, [cellId, activeGroup?.cellIds])

  useEffect(() => {
    if (pinnedGroupId) {
      const groupName = findGroupName(pinnedGroupId, pinnedCells)
      if (groupName) {
        setPinnedGroupName(groupName)
      }
    }
  }, [pinnedCells, pinnedGroupId])

  // Early return for skeleton
  if (!imageDataUrl) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', p: '2px' }} {...wrapperProps}>
        <Skeleton
          data-testid="ActionPlotCellImageSkeleton"
          variant="rectangular"
          width={imgProps?.width ?? defaultImageSize}
          height={imgProps?.height ?? defaultImageSize}
          style={imgProps?.style}
        />
      </Box>
    )
  }

  return (
    <Box component="span" sx={{ display: 'inline-flex', p: '2px' }} {...wrapperProps}>
      <Box sx={{ position: 'relative', overflow: 'hidden' }} onClick={handleCellClicked}>
        {showChecked && (
          <Checkbox
            disableTouchRipple
            checkedIcon={<CheckedIcon inheritViewBox sx={{ width: '22px', height: '22px' }} />}
            sx={{ p: 0, position: 'absolute', zIndex: '2' }}
            checked
          />
        )}
        <img
          data-testid="ActionPlotCellImageImgElement"
          src={imageDataUrl}
          width={imgProps?.width ?? defaultImageSize}
          alt=""
          loading="lazy"
          {...imgProps}
        />
      </Box>
    </Box>
  )
}

ActionPlotCellImageComponent.displayName = 'ActionPlotCellImage'

export const ActionPlotCellImage = memo(ActionPlotCellImageComponent)

export default ActionPlotCellImage